import { useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import { Grid } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import BasicDialog from "../../base/BasicDialog";
import LoaderWrapper from "../../wrapper/LoaderWrapper";

import { useForm } from "../../../hooks/useForm";

import AutocompleteField from "../../field/AutocompleteField";
import DateField from "../../field/DateField/DateField";
import FloatField from "../../base/FloatField/FloatField";

import DateService from "../../../services/dateService";
import useEquipmentService from "../../../services/equipmentService";

import { useAsync, useAsyncFn } from "../../../hooks/useAsync";
import { useSnackbarAlert } from "../../../context/snackbarAlert";

import { getErrorMsg, isEmptyValue } from "../../../helpers/methods";

import {
  DIALOG_PREVIEW_MODE,
  DIALOG_EDIT_MODE,
  DIALOG_CREATE_MODE,
} from "../../../helpers/constants";

const REQUIRED_FIELDS = [
  "edmp_period",
  "equipment",
  "edmp_morning_peak_value",
  "edmp_afternoon_peak_value",
  "edmp_rest_of_the_day_value",
  "edmp_rush_value",
  "edmp_norush_value",
  "edmp_rush_hours_factor",
  "edmp_norush_hours_factor",
];

const FIELDS_TO_SAVE = [...REQUIRED_FIELDS];

const EquipmentDifferentialMonthParamsDetailsDialog = (props) => {
  const { t } = useTranslation();
  const snackbarAlert = useSnackbarAlert();

  const dialogTitle = useMemo(() => {
    return props.dialogMode === DIALOG_CREATE_MODE
      ? t("dialog.equipment.add_edmp")
      : props.dialogMode === DIALOG_EDIT_MODE
      ? t("dialog.equipment.edit_edmp")
      : t("dialog.equipment.edmp_details");
  }, [props.dialogMode]);

  const isReadOnly = useMemo(
    () => props.readOnly || props.dialogMode === DIALOG_PREVIEW_MODE,
    [props.readOnly, props.dialogMode]
  );

  const fieldsToSave = useMemo(
    () =>
      props.dialogMode === DIALOG_CREATE_MODE
        ? FIELDS_TO_SAVE
        : FIELDS_TO_SAVE.filter((field) => field !== "edmp_period"),
    [props.fieldsToSave]
  );

  const {
    formValue,
    setFormValue,
    onChange,
    onChangeDate,
    onChangeAutocompleteFieldWithObjectOptions,
  } = useForm();

  const handleChangeEdmpPeriod = (e) => {
    setFormValue((prev) => ({
      ...prev,
      edmp_period: DateService.convertDateToFormatYYYYMMDD(e.target.value),
    }));
  };

  const { getEdmpDetailsData, createEdmp, updateEdmp, getEdmpFilteringData } =
    useEquipmentService();

  const initialData = useAsync(() => {
    if (props.dialogMode === DIALOG_CREATE_MODE) {
      return Promise.resolve({
        edmp_period: DateService.convertDateToFormatYYYYMMDD(
          DateService.getPreviousMonth()
        ),
      });
    }

    return getEdmpDetailsData(props.edmpId);
  }, [props.getEdmpDetailsData, props.dialogMode]);

  const filteringData = useAsync(getEdmpFilteringData);

  useEffect(() => {
    setFormValue(initialData.value);
  }, [initialData.loading]);

  const prepareDataToSend = () => {
    const dataToSend = {};
    for (const [key, value] of Object.entries(formValue)) {
      if (fieldsToSave.includes(key)) {
        dataToSend[key] = value;
      }
    }
    return dataToSend;
  };

  const updateEdmpFn = useAsyncFn(updateEdmp);
  const addEdmpFn = useAsyncFn(createEdmp);
  const submitFn = useMemo(
    () => (props.dialogMode === DIALOG_CREATE_MODE ? addEdmpFn : updateEdmpFn),
    [props.dialogMode]
  );

  const handleSubmit = (dataToSend) => {
    submitFn
      .execute(dataToSend, props.edmpId)
      .then((res) => {
        if (props.dialogMode === DIALOG_CREATE_MODE) {
          snackbarAlert.openSuccessSnackbarAlert(
            t("snackbar_alert.edmp_created")
          );
        } else {
          snackbarAlert.openSuccessSnackbarAlert(
            t("snackbar_alert.edmp_updated")
          );
        }
        if (props.onSubmitCallback) {
          props.onSubmitCallback();
        }
        props.onClose();
      })
      .catch((error) => {
        snackbarAlert.openErrorSnackbarAlert(
          getErrorMsg(
            error.data,
            t("snackbar_alert.occurred_error_during_saving_changes")
          )
        );
      });
  };

  const isFormValid =
    formValue &&
    REQUIRED_FIELDS.every((field) => !isEmptyValue(formValue[field]));

  const isLoading = formValue === undefined || filteringData.loading;

  const getDialogContent = () => {
    if (isLoading) return <LoaderWrapper showLoader={true} />;

    return (
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        rowGap={1}
        columnSpacing={1}
      >
        <Grid item xs={12}>
          <DateField
            label={t("dialog.equipment.edmp_period")}
            value={formValue.edmp_period}
            onChange={handleChangeEdmpPeriod}
            showClearIcon={false}
            view={"month"}
            readOnly={props.dialogMode === DIALOG_EDIT_MODE}
          />
        </Grid>
        <Grid item xs={12}>
          <AutocompleteField
            name="equipment"
            isObjectOption={true}
            value={formValue.equipment}
            options={filteringData.value?.equipment}
            optionLabelKey={"equipment_nr_with_building"}
            label={t("dialog.equipment.equipment")}
            onChange={onChangeAutocompleteFieldWithObjectOptions}
            multiple={false}
            disableClearable
            readOnly={props.dialogMode === DIALOG_EDIT_MODE}
          />
        </Grid>
        <Grid item xs={12}>
          <FloatField
            name="edmp_morning_peak_value"
            label={t("dialog.equipment.edmp_morning_peak_value")}
            value={formValue?.edmp_morning_peak_value}
            required
            decimalPlaces={2}
            onChange={onChange}
          />
        </Grid>
        <Grid item xs={12}>
          <FloatField
            name="edmp_afternoon_peak_value"
            label={t("dialog.equipment.edmp_afternoon_peak_value")}
            value={formValue?.edmp_afternoon_peak_value}
            required
            decimalPlaces={2}
            onChange={onChange}
          />
        </Grid>
        <Grid item xs={12}>
          <FloatField
            name="edmp_rest_of_the_day_value"
            label={t("dialog.equipment.edmp_rest_of_the_day_value")}
            value={formValue?.edmp_rest_of_the_day_value}
            required
            decimalPlaces={2}
            onChange={onChange}
          />
        </Grid>
        <Grid item xs={12}>
          <FloatField
            name="edmp_rush_value"
            label={t("dialog.equipment.edmp_rush_value")}
            value={formValue?.edmp_rush_value}
            required
            decimalPlaces={2}
            onChange={onChange}
          />
        </Grid>
        <Grid item xs={12}>
          <FloatField
            name="edmp_norush_value"
            label={t("dialog.equipment.edmp_norush_value")}
            value={formValue?.edmp_norush_value}
            required
            decimalPlaces={2}
            onChange={onChange}
          />
        </Grid>
        <Grid item xs={12}>
          <FloatField
            name="edmp_rush_hours_factor"
            label={t("dialog.equipment.edmp_rush_hours_factor")}
            value={formValue?.edmp_rush_hours_factor}
            required
            decimalPlaces={4}
            onChange={onChange}
          />
        </Grid>
        <Grid item xs={12}>
          <FloatField
            name="edmp_norush_hours_factor"
            label={t("dialog.equipment.edmp_norush_hours_factor")}
            value={formValue?.edmp_norush_hours_factor}
            required
            decimalPlaces={4}
            onChange={onChange}
          />
        </Grid>

        {!isReadOnly && (
          <Grid item xs={12}>
            <LoadingButton
              variant="contained"
              color="primary"
              fullWidth
              loading={addEdmpFn.loading || updateEdmpFn.loading}
              disabled={!isFormValid}
              onClick={() => handleSubmit(prepareDataToSend())}
            >
              {t("save")}
            </LoadingButton>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <BasicDialog
      open={props.open}
      onClose={props.onClose}
      titleAlign="center"
      contentAlign="center"
      showTopFullScreenButton={false}
      title={dialogTitle}
      maxWidth="xs"
      showDialogActions
    >
      {getDialogContent()}
    </BasicDialog>
  );
};

EquipmentDifferentialMonthParamsDetailsDialog.propTypes = {
  dialogMode: PropTypes.oneOf([
    DIALOG_CREATE_MODE,
    DIALOG_EDIT_MODE,
    DIALOG_PREVIEW_MODE,
  ]),
  readOnly: PropTypes.bool,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmitCallback: PropTypes.func,
  fieldsToSave: PropTypes.array,
};

EquipmentDifferentialMonthParamsDetailsDialog.defaultProps = {
  open: false,
};

export default EquipmentDifferentialMonthParamsDetailsDialog;
